/**
 * useMaestroChat — Hook managing Maestro terminal chat state
 * MAESTRO:P0D
 *
 * Manages:
 *  - activeConversation, messages, isLoading
 *  - sendMessage() — posts message, appends optimistic user bubble, replaces with real response
 *  - loadConversation(id) — hydrates messages from an existing conversation
 *  - newConversation() — resets state for a fresh session
 *  - conversationList — paginated sidebar list with loadMore()
 *
 * AbortController pattern mirrors useProducts.js.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { maestroApi } from '../services/maestroApi';

const LIST_PAGE_SIZE = 20;

export function useMaestroChat() {
  // ─── Active conversation ─────────────────────────────────────────────────
  const [activeConversation, setActiveConversation] = useState(null); // { id, title, ... }
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // ─── P1: Streaming state ───────────────────────────────────────────────
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [streamingEvent, setStreamingEvent] = useState(null);
  const [activeEntities, setActiveEntities] = useState([]);
  const streamAbortRef = useRef(null);

  // ─── Conversation list (sidebar) ─────────────────────────────────────────
  const [conversationList, setConversationList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listOffset, setListOffset] = useState(0);
  const [listHasMore, setListHasMore] = useState(true);

  // Abort controller refs
  const sendAbortRef = useRef(null);
  const loadAbortRef = useRef(null);
  const listAbortRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      sendAbortRef.current?.abort();
      loadAbortRef.current?.abort();
      listAbortRef.current?.abort();
    };
  }, []);

  // ─── Load conversation list ───────────────────────────────────────────────
  const loadConversationList = useCallback(async (reset = false) => {
    /* v8 ignore next */
    if (!mountedRef.current) return;

    // Cancel any in-flight list request
    listAbortRef.current?.abort();
    listAbortRef.current = new AbortController();

    const offset = reset ? 0 : listOffset;
    setListLoading(true);

    try {
      const data = await maestroApi.getConversations(LIST_PAGE_SIZE, offset);
      if (!mountedRef.current) return;

      const items = data?.conversations ?? [];
      setConversationList(prev => reset ? items : [...prev, ...items]);
      setListOffset(offset + items.length);
      setListHasMore(data?.has_more ?? false);
    } catch (err) {
      if (err?.name === 'AbortError' || !mountedRef.current) return;
      // Silently fail sidebar list — non-fatal
      if (import.meta.env.DEV) console.warn('[useMaestroChat] list error:', err);
    } finally {
      if (mountedRef.current) setListLoading(false);
    }
  }, [listOffset]);

  // Stable ref to avoid re-triggering effect when listOffset changes
  const loadConversationListRef = useRef(loadConversationList);
  loadConversationListRef.current = loadConversationList;

  // Initial list load
  useEffect(() => {
    loadConversationListRef.current(true);
  }, []);

  // ─── Load an existing conversation ──────────────────────────────────────
  const loadConversation = useCallback(async (id) => {
    if (!id || !mountedRef.current) return;

    // Cancel any in-flight load
    loadAbortRef.current?.abort();
    loadAbortRef.current = new AbortController();

    setIsLoading(true);
    setError(null);
    setMessages([]);

    try {
      const [conv, msgData] = await Promise.all([
        maestroApi.getConversation(id),
        maestroApi.getMessages(id),
      ]);
      if (!mountedRef.current) return;

      setActiveConversation(conv);
      setMessages(msgData?.messages ?? []);
    } catch (err) {
      if (err?.name === 'AbortError' || !mountedRef.current) return;
      setError(err.message || 'Failed to load conversation');
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, []);

  // ─── Reset to fresh conversation ─────────────────────────────────────────
  const newConversation = useCallback(() => {
    sendAbortRef.current?.abort();
    loadAbortRef.current?.abort();
    setActiveConversation(null);
    setMessages([]);
    setError(null);
    setIsLoading(false);
  }, []);

  // ─── Send a message ──────────────────────────────────────────────────────
  const sendMessage = useCallback(async (text, context = null) => {
    if (!text?.trim() || isLoading || !mountedRef.current) return;

    // Cancel any previous in-flight send
    sendAbortRef.current?.abort();
    sendAbortRef.current = new AbortController();

    const optimisticId = `opt-${Date.now()}`;
    const userMessage = {
      id: optimisticId,
      role: 'user',
      content: text.trim(),
      created_at: new Date().toISOString(),
    };

    // Optimistic update — append user bubble immediately
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setError(null);

    try {
      const conversationId = activeConversation?.id ?? null;
      const response = await maestroApi.sendMessage(text.trim(), conversationId, context);
      if (!mountedRef.current) return;

      const assistantMessage = {
        id: response.message_id,
        role: 'assistant',
        content: response.content,
        structured_data: response.structured_data ?? null,
        suggested_followups: response.suggested_followups ?? [],
        response_time_ms: response.response_time_ms ?? null,
        intent_type: response.intent_type ?? null,
        created_at: new Date().toISOString(),
      };

      // If this was a new conversation, store the returned conversation id
      if (!activeConversation) {
        setActiveConversation({ id: response.conversation_id, title: text.trim().slice(0, 60) });
        // Refresh sidebar to include new conversation
        loadConversationList(true);
      }

      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      if (err?.name === 'AbortError' || !mountedRef.current) return;
      setError(err.message || 'Failed to send message');
      // Remove optimistic user bubble on failure
      setMessages(prev => prev.filter(m => m.id !== optimisticId));
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [isLoading, activeConversation, loadConversationList]);

  // ─── Archive a conversation ───────────────────────────────────────────────
  const archiveConversation = useCallback(async (id) => {
    try {
      await maestroApi.archiveConversation(id);
      if (!mountedRef.current) return;

      // Remove from list
      setConversationList(prev => prev.filter(c => c.id !== id));

      // If currently active, reset
      if (activeConversation?.id === id) {
        newConversation();
      }
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[useMaestroChat] archive error:', err);
    }
  }, [activeConversation, newConversation]);

  // ─── Load more conversations (pagination) ────────────────────────────────
  const loadMoreConversations = useCallback(() => {
    if (!listHasMore || listLoading) return;
    loadConversationList(false);
  }, [listHasMore, listLoading, loadConversationList]);

  return {
    // Active chat state
    activeConversation,
    messages,
    isLoading,
    error,

    // Actions
    sendMessage,
    loadConversation,
    newConversation,
    archiveConversation,

    // Sidebar list
    conversationList,
    listLoading,
    listHasMore,
    loadMoreConversations,

    // P1: Streaming state
    isStreaming,
    streamingText,
    streamingEvent,
    activeEntities,
    setActiveEntities,
  };
}

export default useMaestroChat;
